import type { GalleryImage, GalleryPhoto } from "./gallery";
import { galleryItemKind } from "./gallery.kind";

/** One stop in the lightbox: a photo, and the tile it belongs to. */
interface GalleryPhotoStop {
  photo: GalleryPhoto;
  imageIndex: number;
  photoIndex: number;
}

/**
 * What the lightbox pages through at one time.
 *
 * A photo set is its own group; every loose tile (a single photo, a
 * comparison) shares one group, in tile order. Paging the whole section as one
 * sequence meant opening a photo of one project and scrolling into another
 * project's photos, under a count and a thumbnail strip describing neither.
 * The group is the scope of the arrows, the counter and the thumbnails, so a
 * visitor is always inside exactly one project — or outside all of them.
 */
interface GalleryGroup {
  /** "set" is one project's photos; "photos" is every loose tile. */
  kind: "set" | "photos";
  /** The group's own name, "" when it has none: the loose group is not a
   *  project, and a set is only named if a pro wrote something short on it.
   *  What to call an unnamed group is the lightbox's business, not this. */
  label: string;
  stops: GalleryPhotoStop[];
}

/**
 * Past this a label is a sentence, not a name. Alt text is written to describe
 * a photo to a screen reader ("A waterfall cascades into a moss-covered canyon
 * surrounded by green hills and vegetation"), which reads as nonsense where a
 * project's name belongs, so a set that long-winded is treated as unnamed.
 */
const MAX_SET_LABEL = 40,

  /**
   * Every photo for one tile, cover first. The cover is always index 0 so the lightbox
   * opens on the image that was actually clicked.
   */
  getProjectPhotos = (image: GalleryImage): GalleryPhoto[] => {
    const cover = image.imageUrl || image.afterImageUrl || image.beforeImageUrl,
      // A photo is one photo: extra photos belong to a photo set, and paging
      // Through leftovers a pro can no longer see in the panel would be a ghost.
      rest =
        galleryItemKind(image) === "photo"
          ? []
          : (image.photos ?? []).filter((photo) => photo?.imageUrl);

    if (!cover) {
      return rest;
    }

    return [
      { alt: image.alt, caption: image.caption, imageUrl: cover },
      ...rest.filter((photo) => photo.imageUrl !== cover),
    ];
  },

  /** A set's own name, or "" when nothing on it reads as one. */
  setLabel = (image: GalleryImage): string => {
    for (const candidate of [image.caption, image.tag, image.alt]) {
      const text = candidate?.trim();
      if (text && text.length <= MAX_SET_LABEL) {
        return text;
      }
    }
    return "";
  },

  /**
   * The section's photos as groups, in tile order. The loose group takes the
   * position of the first loose tile, so a gallery that opens with single photos
   * lists them first and one that opens with a project lists that project first.
   */
  photoGroupsOf = (images: GalleryImage[]): GalleryGroup[] => {
    const groups: GalleryGroup[] = [];
    let loose: GalleryGroup | null = null;

    images.forEach((image, imageIndex) => {
      const photos = getProjectPhotos(image);
      if (photos.length === 0) {
        return;
      }
      const stops = photos.map((photo, photoIndex) => ({ imageIndex, photo, photoIndex }));

      if (galleryItemKind(image) === "project") {
        groups.push({ kind: "set", label: setLabel(image), stops });
        return;
      }

      if (!loose) {
        loose = { kind: "photos", label: "", stops: [] };
        groups.push(loose);
      }
      loose.stops.push(...stops);
    });

    return groups;
  },
  /** Every view in order: each group's photos, then its end card. */
  galleryViewsOf = (groups: GalleryGroup[]): GalleryView[] => {
    const hasEndCards = groups.length > 1;
    return groups.flatMap((group, groupIndex) => {
      const photos = group.stops.map(
        (_, stopIndex): GalleryView => ({ groupIndex, kind: "photo", stopIndex }),
      );
      if (!hasEndCards) {
        return photos;
      }
      return [
        ...photos,
        {
          groupIndex,
          kind: "end" as const,
          // The last group's card points back at the first: paging is a loop
          // Already, and an end that offers nothing is a dead end.
          nextGroupIndex: (groupIndex + 1) % groups.length,
        },
      ];
    });
  },
  /** The view a click on tile `imageIndex` opens: that tile's own photo. */
  viewIndexForImage = (views: GalleryView[], groups: GalleryGroup[], imageIndex: number): number => views.findIndex(
    (view) =>
      view.kind === "photo" &&
      groups[view.groupIndex]?.stops[view.stopIndex]?.imageIndex === imageIndex &&
      groups[view.groupIndex]?.stops[view.stopIndex]?.photoIndex === 0,
  ),
  /** The view showing stop `stopIndex` of group `groupIndex` (thumbnail clicks). */
  viewIndexForStop = (
    views: GalleryView[],
    groupIndex: number,
    stopIndex: number,
  ): number => views.findIndex(
    (view) =>
      view.kind === "photo" && view.groupIndex === groupIndex && view.stopIndex === stopIndex,
  );

/**
 * One screen of the lightbox: a photo of a group, or the card that closes a
 * group and names the next one. The end card only exists when there IS a next
 * group, so a gallery that is one project (or all loose photos) pages exactly
 * as it did before groups.
 */
type GalleryView =
  | { kind: "photo"; groupIndex: number; stopIndex: number }
  | { kind: "end"; groupIndex: number; nextGroupIndex: number };

export type { GalleryPhotoStop, GalleryGroup, GalleryView };
export { getProjectPhotos, photoGroupsOf, galleryViewsOf, viewIndexForImage, viewIndexForStop };
